import React from 'react';
import {Box, Chip, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import InstalledBaseNodeTypeIcon from "../../InstalledBaseNodeTypeIcon";

const INDENT = 20;

/**
 * Explains which node a system grid needs, as a three-level sample hierarchy:
 * the grid loads the two levels below the selected node — every child becomes
 * a box in the grid and its assets are shown inside that box.
 */
const NodeSelectionHint = () => {
    const {t} = useTranslation();

    const levels = [
        {nodeTypeKey: 'system', label: t('System'), role: t('Your selection'), selected: true},
        {nodeTypeKey: 'pressure_line', label: t('Pressure line'), role: t('Box in the grid')},
        {nodeTypeKey: 'asset', label: t('Asset'), role: t('Shown in its box')},
    ];

    return (
        <Box
            data-testid="grid-node-selection-hint"
            borderRadius="4px"
            border="1px solid #ccc"
            padding="12px 16px"
            marginTop="8px"
            marginBottom="12px"
        >
            <Typography level="body" sx={{mb: '8px'}}>
                {t("Select a system. The grid shows the two levels below it.")}
            </Typography>
            <Box display="flex" flexDirection="column" gap="6px">
                {levels.map((level, index) => (
                    <Box key={level.nodeTypeKey} display="flex" alignItems="center" gap="8px" minWidth={0}>
                        <Box display="flex" alignItems="center" flexShrink={0} sx={{pl: `${Math.max(index - 1, 0) * INDENT}px`}}>
                            {index > 0 && (
                                // Tree connector to the level above
                                <Box
                                    sx={{
                                        width: '12px',
                                        height: '12px',
                                        mr: '4px',
                                        mt: '-8px',
                                        borderLeft: '1px solid #ccc',
                                        borderBottom: '1px solid #ccc',
                                    }}
                                />
                            )}
                            <InstalledBaseNodeTypeIcon nodeTypeKey={level.nodeTypeKey} size={18}/>
                        </Box>
                        <Typography
                            level="body"
                            noWrap
                            fontWeight={level.selected ? 'bold' : undefined}
                            sx={{minWidth: 0}}
                        >
                            {level.label}
                        </Typography>
                        <Chip
                            size="sm"
                            variant="soft"
                            color={level.selected ? 'primary' : 'neutral'}
                            sx={{flexShrink: 0, ml: 'auto'}}
                        >
                            {level.role}
                        </Chip>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default NodeSelectionHint;
